import React from "react";
import PlayCircleOutlineRoundedIcon from "@mui/icons-material/PlayCircleOutlineRounded";
import { alpha, Box, Button, Stack, Typography, useTheme } from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import ImageBackground from "../../../../utility/component/ImageBackground";
import { getCourseGlassSurfaceSx, getCourseMediaFrameSx } from "./const/surface";

export type MediaTileProps = Readonly<{
  title: string;
  subtitle?: string;
  thumbnailImage: string;
  isVideo?: boolean;
  isPlaying?: boolean;
  onPlayToggle?: () => void;
  embedUrl?: string;
  actionLabel: string;
  onAction: () => void;
  actionIcon?: React.ReactNode;
  rootSx?: SxProps<Theme>;
  mediaSx?: SxProps<Theme>;
  fillHeight?: boolean;
}>;

export default function MediaTile({
  title,
  subtitle,
  thumbnailImage,
  isVideo,
  isPlaying,
  onPlayToggle,
  embedUrl,
  actionLabel,
  onAction,
  actionIcon,
  rootSx,
  mediaSx,
  fillHeight,
}: MediaTileProps) {
  const theme = useTheme();

  return (
    <Stack
      spacing={0}
      sx={{
        p: 1.25,
        ...getCourseGlassSurfaceSx(theme, "strong"),
        height: fillHeight ? 1 : "auto",
        minHeight: fillHeight ? 0 : undefined,
        ...rootSx,
      }}
    >
      <Box
        component={isVideo ? "button" : "div"}
        onClick={!isVideo ? undefined : onPlayToggle}
        sx={{
          appearance: "none",
          textAlign: "left",
          cursor: !isVideo ? "default" : "pointer",
          width: 1,
          height: fillHeight ? 1 : "auto",
          minHeight: fillHeight ? 0 : undefined,
          display: fillHeight ? "flex" : "block",
          flexDirection: fillHeight ? "column" : undefined,
          p: 0,
          border: 0,
          bgcolor: "transparent",
        }}
      >
        <Box
          sx={{
            position: "relative",
            overflow: "hidden",
            ...(fillHeight
              ? {
                  flex: 1,
                  minHeight: 0,
                  height: { xs: "auto", md: 1 },
                  aspectRatio: { xs: "16 / 9", md: "auto" },
                }
              : { aspectRatio: "16 / 9" }),
            ...getCourseMediaFrameSx(theme),
            ...mediaSx,
          }}
        >
          {!isVideo || !isPlaying ? (
            <ImageBackground
              image={thumbnailImage}
              center="center"
              sx={{
                width: 1,
                height: 1,
                "&::after": {
                  content: '""',
                  position: "absolute",
                  inset: 0,
                  background: `linear-gradient(180deg, ${alpha(theme.palette.primary.dark, 0.08)} 0%, ${alpha(
                    theme.palette.primary.dark,
                    0.58,
                  )} 100%)`,
                },
              }}
            />
          ) : null}

          {!isVideo ? null : isPlaying ? (
            <Box
              component="iframe"
              title={title}
              src={embedUrl ?? ""}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              sx={{
                position: "absolute",
                inset: 0,
                width: 1,
                height: 1,
                border: 0,
              }}
            />
          ) : (
            <Stack sx={{ position: "absolute", inset: 0, alignItems: "center", justifyContent: "center" }}>
              <PlayCircleOutlineRoundedIcon
                sx={{
                  fontSize: 72,
                  color: alpha(theme.palette.primary.contrastText, 0.92),
                }}
              />
            </Stack>
          )}
        </Box>
      </Box>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={2}
        sx={{ pt: 1.75, pb: 0.5, px: 0.5 }}
      >
        <Stack spacing={0.5} sx={{ minWidth: 0 }}>
          <Typography variant="h6" color="primary.contrastText" noWrap>
            {title}
          </Typography>
          {!subtitle ? null : (
            <Typography variant="body2" sx={{ color: alpha(theme.palette.primary.contrastText, 0.64) }} noWrap>
              {subtitle}
            </Typography>
          )}
        </Stack>

        <Button
          variant="outlined"
          startIcon={!actionIcon ? undefined : actionIcon}
          onClick={onAction}
          sx={{
            flexShrink: 0,
            borderRadius: 0,
            borderColor: alpha(theme.palette.primary.contrastText, 0.18),
            color: "primary.contrastText",
            "&:hover": {
              borderColor: alpha(theme.palette.primary.contrastText, 0.36),
              bgcolor: alpha(theme.palette.primary.contrastText, 0.04),
            },
          }}
        >
          {actionLabel}
        </Button>
      </Stack>
    </Stack>
  );
}

